import { query, collection, onSnapshot, setDoc, updateDoc, deleteDoc, doc, getDocs } from "@firebase/firestore";
import { Unsubscribe, where } from "firebase/firestore";
import { db } from "../config/firebase-config";
import { IObjective } from "../models/Objective";
import AppStore from "../stores/AppStore";
import AppApi, { apiPathScorecardLevel } from "./AppApi";

const removeUndefinedFields = <T extends Record<string, unknown>>(item: T): T => {
  return Object.fromEntries(
    Object.entries(item).filter(([, value]) => value !== undefined)
  ) as T;
};

export default class ObjectiveApi {
  // path: string | null = null;

  constructor(private api: AppApi, private store: AppStore) { }


  private getPath() {
    if (!this.store.scorecard.activeId) return null;
    return apiPathScorecardLevel(this.store.scorecard.activeId, "objectives");
  }

  private currentPath(scorecardId = this.store.scorecard.currentId) {
    if (!scorecardId) return;
    return apiPathScorecardLevel(scorecardId, "objectives");
  }

  async getAllObjectives() {
    // get the db path
    const path = this.getPath();
    if (!path) return;
    // remove all items from store
    this.store.objective.removeAll();

    // create the query
    const $query = query(collection(db, path));
    // new promise
    return await new Promise<Unsubscribe>((resolve, reject) => {
      const unsubscribe = onSnapshot($query, (querySnapshot) => {
        const items: IObjective[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ id: doc.id, ...doc.data() } as IObjective);
        });
        this.store.objective.load(items);
        resolve(unsubscribe);
      }, (error) => {
        reject();
      });
    });
  }
  async getAll(uid?: string) {
    // get the db path
    const path = this.getPath();
    if (!path) return;

    // this.store.objective.removeAll();

    // create the query
    const $query = uid ? query(collection(db, path), where("uid", "==", uid)) : query(collection(db, path));
    // new promise
    return await new Promise<Unsubscribe>((resolve, reject) => {
      // on snapshot
      const unsubscribe = onSnapshot(
        $query,
        // onNext
        (querySnapshot) => {
          const items: IObjective[] = [];
          querySnapshot.forEach((doc) => {
            items.push({ id: doc.id, ...doc.data() } as IObjective);
          });
          this.store.objective.load(items);
          resolve(unsubscribe);
        },
        // onError
        (error) => {
          reject();
        }
      );
    });
  }

  async getById(id: string) {
    const path = this.getPath();
    if (!path) return;

    const unsubscribe = onSnapshot(doc(db, path, id), (doc) => {
      if (!doc.exists) return;
      const item = { id: doc.id, ...doc.data() } as IObjective;

      this.store.objective.load([item]);
    });
    return unsubscribe;
  }

  // create objective
  async create(item: IObjective) {
    const path = this.getPath();
    if (!path) throw new Error("Active scorecard path is not available.");

    const itemRef = doc(collection(db, path));
    const itemToCreate = removeUndefinedFields({ ...item, id: itemRef.id });
    await setDoc(itemRef, itemToCreate, { merge: true });
    this.store.objective.load([itemToCreate]);
  }

  // duplicate objective
  async duplicate(item: IObjective, scorecardId?: string) {
    const path = this.currentPath(scorecardId);
    if (!path) throw new Error("Current scorecard path is not available.");

    const itemRef = doc(collection(db, path), item.id);
    // console.log(itemRef.id);
    // console.log(item);

    // create in db
    try {
      await setDoc(itemRef, removeUndefinedFields({ ...item }), { merge: true, });
      this.store.objective.load([item]);
    } catch (error) {
      throw error;
    }
  }

  async getAllByScorecardId(scorecardId: string, uid?: string) {
    const path = apiPathScorecardLevel(scorecardId, "objectives");
    const $query = uid ? query(collection(db, path), where("uid", "==", uid)) : query(collection(db, path));
    const docsSnap = await getDocs($query);
    const items: IObjective[] = [];

    docsSnap.forEach((doc) => {
      items.push({ id: doc.id, ...doc.data() } as IObjective);
    });

    return items;
  }

  // update item
  async update(item: IObjective) {
    const path = this.getPath();
    if (!path) throw new Error("Active scorecard path is not available.");

    const itemToUpdate = removeUndefinedFields({ ...item });
    await updateDoc(doc(db, path, item.id), itemToUpdate);
    this.store.objective.load([itemToUpdate]);
  }

  // delete objective
  async delete(item: IObjective) {
    const path = this.getPath();
    if (!path) return;

    try {
      await deleteDoc(doc(db, path, item.id));
      // remove from store
      this.store.objective.remove(item.id);
    } catch (error) { }
  }
}
